/** @jsxImportSource react */
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { signOut, onAuthStateChanged } from 'firebase/auth'
import { auth } from '@/firebase'
import { BackgroundParticles } from '../shared/BackgroundParticles'
import { LogoInsaite } from '../shared/LogoInsaite'
import { userService } from '@/services/user.service'
import { getRoleFromToken, clearSessionData } from '@/utils/tokenValidation'
import { getTokenForPath } from '@/utils/authToken'
import { PAYMENTS_ENABLED } from '@/config/features'
import { useThemeLanguage } from '@/contexts/ThemeLanguageContext'
import { Breadcrumbs, RecruiterUploadPageTitle } from '../shared/Breadcrumbs'
import { AppModal } from '../shared/AppModal'
import { OnboardingGuide } from '../shared/OnboardingGuide'
import { ImpersonationBanner } from '../shared/ImpersonationBanner'
import { AdminUnifiedSidebarNav } from '../shared/AdminUnifiedSidebarNav'
import { HiringPauseBanner } from '../shared/HiringPauseBanner'
import { useNotifications } from '@/contexts/NotificationContext'
import { NotificationListPanel } from '../shared/NotificationListPanel'
import { WorkspaceLangButton } from '../shared/workspace/WorkspaceLangButton'
import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from '../shared/workspace/workspaceSidebarConstants'
import './RecruiterLayout.css'
import './RecruiterModals.css'
import '../admin/AdminLayout.css'

function getInitials(name: string): string {
  if (!name) return '?'
  const names = name.trim().split(/\s+/)
  const initials = names.map((n) => n[0]).join('')
  return initials.slice(0, 2).toUpperCase()
}

// ── Static SVG icons (hoisted outside component to avoid re-creation) ──
const collapseIconSvg = React.createElement(
  'svg',
  {
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    width: 20,
    height: 20,
  },
  React.createElement('path', {
    d: 'M15 18L9 12L15 6',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  }),
)
const expandIconSvg = React.createElement(
  'svg',
  {
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    width: 20,
    height: 20,
  },
  React.createElement('path', {
    d: 'M3 12H21M3 6H21M3 18H21',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  }),
)
const dashboardIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' },
  React.createElement('rect', {
    x: '3',
    y: '3',
    width: '7',
    height: '7',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    fill: 'none',
  }),
  React.createElement('rect', {
    x: '14',
    y: '3',
    width: '7',
    height: '7',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    fill: 'none',
  }),
  React.createElement('rect', {
    x: '3',
    y: '14',
    width: '7',
    height: '7',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    fill: 'none',
  }),
  React.createElement('rect', {
    x: '14',
    y: '14',
    width: '7',
    height: '7',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    fill: 'none',
  }),
)
const jdUploadIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' },
  React.createElement('path', {
    d: 'M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    fill: 'none',
  }),
  React.createElement('path', {
    d: 'M14 2V8H20',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    strokeLinecap: 'round',
  }),
  React.createElement('path', {
    d: 'M8 13H16',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    strokeLinecap: 'round',
  }),
  React.createElement('path', {
    d: 'M8 17H16',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    strokeLinecap: 'round',
  }),
)
const premiumIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' },
  React.createElement('path', {
    d: 'M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    fill: 'none',
  }),
)
const settingsIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' },
  React.createElement('circle', {
    cx: '12',
    cy: '12',
    r: '3',
    stroke: 'currentColor',
    strokeWidth: '2',
    fill: 'none',
  }),
  React.createElement('path', {
    d: 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z',
    stroke: 'currentColor',
    strokeWidth: '2',
    fill: 'none',
  }),
)
const logoutIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' },
  React.createElement('path', {
    d: 'M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    strokeLinecap: 'round',
  }),
  React.createElement('path', {
    d: 'M16 17L21 12L16 7',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  }),
  React.createElement('path', {
    d: 'M21 12H9',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    strokeLinecap: 'round',
  }),
)
const bellIconSvg = React.createElement(
  'svg',
  {
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    width: 22,
    height: 22,
  },
  React.createElement('path', {
    d: 'M18 8A6 6 0 0 0 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  }),
  React.createElement('path', {
    d: 'M13.73 21a2 2 0 0 1-3.46 0',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  }),
)

export function RecruiterLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const esAdmin = useMemo(() => {
    const token = getTokenForPath(location.pathname)
    return !!token && getRoleFromToken(token) === 'admin'
  }, [location.pathname])
  const isAdminRoute = useMemo(() => location.pathname.startsWith('/admin'), [location.pathname])
  const { t, theme } = useThemeLanguage()
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications()
  const [showNotifications, setShowNotifications] = useState(false)
  const [isPremium, setIsPremium] = useState(false)
  const [pulsingPath, setPulsingPath] = useState<string | null>(null)
  const [user, setUser] = useState<{
    displayName?: string | null
    email?: string | null
    photoURL?: string | null
  } | null>(null)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('recruiter_sidebar_collapsed') === 'true'
    } catch {
      return false
    }
  })

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem('recruiter_sidebar_collapsed', String(next))
      } catch {}
      return next
    })
  }, [])

  const sidebarWidth = isSidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED

  useEffect(() => {
    userService.getProfile().then((profile) => {
      if (profile) setIsPremium(profile.is_premium)
    })
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u ? { displayName: u.displayName, email: u.email, photoURL: u.photoURL } : null)
    })
    return () => unsubscribe()
  }, [])

  const handleNavClick = useCallback(
    (path: string) => {
      setPulsingPath(path)
      if (path === '/recruiter/new') {
        navigate(path, { state: { reset: true } })
      } else {
        navigate(path)
      }
    },
    [navigate],
  )

  useEffect(() => {
    if (!pulsingPath) return
    const t = setTimeout(() => setPulsingPath(null), 350)
    return () => clearTimeout(t)
  }, [pulsingPath])

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return
    setIsLoggingOut(true)
    try {
      await signOut(auth)
      clearSessionData()
      window.location.href = '/'
    } catch (e) {
      console.warn('Firebase signOut:', e)
    } finally {
      setIsLoggingOut(false)
    }
  }, [isLoggingOut])

  const navItem = useCallback(
    (path: string, label: string, iconSvg: React.ReactNode, exact?: boolean) => {
      const active = exact ? location.pathname === path : location.pathname.startsWith(path)
      const isPulsing = path === pulsingPath
      return React.createElement(
        'button',
        {
          type: 'button',
          key: path,
          className: `recruiter-sidebar-nav-item ${active ? 'active' : ''} ${isPulsing ? 'recruiter-sidebar-nav-item--pulse' : ''}`,
          onClick: () => handleNavClick(path),
          title: isSidebarCollapsed ? label : undefined,
        },
        isSidebarCollapsed
          ? React.createElement('span', { className: 'nav-item-icon' }, iconSvg)
          : React.createElement(
              React.Fragment,
              null,
              React.createElement(
                'span',
                { className: 'nav-item-icon nav-item-icon--expanded' },
                iconSvg,
              ),
              React.createElement('span', { className: 'nav-item-text' }, label),
            ),
      )
    },
    [location.pathname, pulsingPath, isSidebarCollapsed, handleNavClick],
  )

  const accountLabel = useMemo(
    () => user?.displayName || user?.email || t('sidebar.account'),
    [user, t],
  )
  const accountAvatar = useMemo(
    () =>
      user?.photoURL
        ? React.createElement('img', {
            src: user.photoURL,
            alt: '',
            className: 'recruiter-sidebar-account-avatar',
          })
        : React.createElement(
            'div',
            { className: 'recruiter-sidebar-account-initials' },
            getInitials(user?.displayName || user?.email || ''),
          ),
    [user],
  )

  const isRecruiterUploadRoute =
    !isAdminRoute &&
    (location.pathname === '/recruiter/new' || location.pathname === '/recruiter/new/')

  const layoutClassName = [
    'recruiter-layout',
    isSidebarCollapsed ? 'sidebar-collapsed' : '',
    isAdminRoute ? 'admin-layout-wrap' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      data-theme={isAdminRoute ? theme : undefined}
      className={layoutClassName}
      style={{ '--sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}
    >
      <div className="recruiter-layout-l-bg">
        <BackgroundParticles
          intensity={0.7}
          particleCount={40}
          colorSet={
            theme === 'violet' ? 'white' : theme === 'violet-inverted' ? 'violet' : 'default'
          }
        />
      </div>

      <aside
        data-testid="recruiter-sidebar-shell"
        className={`recruiter-sidebar recruiter-sidebar--no-bg ${isSidebarCollapsed ? 'collapsed' : ''}`}
      >
        <div className="recruiter-sidebar-header">
          <button
            type="button"
            className="recruiter-sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={isSidebarCollapsed ? t('layout.expandMenu') : t('layout.collapseMenu')}
            title={isSidebarCollapsed ? t('layout.expandMenu') : t('layout.collapseMenu')}
          >
            {isSidebarCollapsed ? expandIconSvg : collapseIconSvg}
          </button>

          {!isSidebarCollapsed && (
            <div className={`recruiter-sidebar-logo recruiter-sidebar-logo--${theme}`}>
              <LogoInsaite
                size="medium"
                position="relative"
                variant={theme === 'light-pure' ? 'dark' : 'light'}
              />
            </div>
          )}

          {isSidebarCollapsed && (
            <div className={`recruiter-sidebar-logo recruiter-sidebar-logo--${theme}`}>
              <LogoInsaite
                size="small"
                position="relative"
                variant={theme === 'light-pure' ? 'dark' : 'light'}
                aiOnly
              />
            </div>
          )}
        </div>

        <div className="recruiter-sidebar-spacer" />

        <div className="recruiter-sidebar-bottom">
          <nav className="recruiter-sidebar-nav">
            {esAdmin
              ? React.createElement(AdminUnifiedSidebarNav, {
                  location,
                  isSidebarCollapsed,
                  onNavigate: handleNavClick,
                  t,
                  isPremium,
                  pulsingPath,
                })
              : React.createElement(
                  React.Fragment,
                  null,
                  navItem('/recruiter', t('sidebar.panel'), dashboardIconSvg, true),
                  navItem('/recruiter/new', t('sidebar.new'), jdUploadIconSvg),
                  PAYMENTS_ENABLED &&
                    !isPremium &&
                    navItem('/recruiter/checkout', t('sidebar.premium'), premiumIconSvg),
                  isPremium &&
                    React.createElement(
                      'div',
                      { className: 'recruiter-sidebar-pro-badge' },
                      React.createElement('span', { className: 'badge-icon' }, '⭐'),
                      !isSidebarCollapsed &&
                        React.createElement('span', { className: 'badge-text' }, 'PRO'),
                    ),
                  navItem('/recruiter/settings', t('sidebar.settings'), settingsIconSvg),
                )}
          </nav>

          <div className="recruiter-sidebar-footer">
            {user && (
              <div
                className={`recruiter-sidebar-account ${isSidebarCollapsed ? 'recruiter-sidebar-account--collapsed' : ''}`}
              >
                <div className="recruiter-sidebar-account-avatar-wrap">{accountAvatar}</div>
                {!isSidebarCollapsed && (
                  <span className="recruiter-sidebar-account-label">{accountLabel}</span>
                )}
              </div>
            )}
            <button
              type="button"
              className="recruiter-sidebar-nav-item recruiter-sidebar-logout"
              onClick={() => setShowLogoutModal(true)}
              disabled={isLoggingOut}
              title={isSidebarCollapsed ? t('sidebar.logout') : undefined}
            >
              {isSidebarCollapsed ? (
                <span className="nav-item-icon">{logoutIconSvg}</span>
              ) : (
                <span className="nav-item-text">
                  {isLoggingOut ? t('sidebar.closing') : t('sidebar.logout')}
                </span>
              )}
            </button>
          </div>
        </div>
      </aside>

      <main className="recruiter-layout-main">
        {!isAdminRoute && React.createElement(ImpersonationBanner)}
        {!isAdminRoute && React.createElement(HiringPauseBanner, { audience: 'recruiter' })}
        <div className={`recruiter-breadcrumbs-wrap ${isAdminRoute ? 'admin-breadcrumbs' : ''}`}>
          {isAdminRoute
            ? React.createElement(
                'span',
                { className: 'admin-breadcrumb-title' },
                t('admin.breadcrumb'),
              )
            : isRecruiterUploadRoute
              ? React.createElement(RecruiterUploadPageTitle)
              : React.createElement(Breadcrumbs)}
          <div className="recruiter-header-actions">
            {React.createElement(WorkspaceLangButton, {
              ariaLabel: isAdminRoute ? t('layout.changeLanguage') : t('candidate.changeLanguage'),
            })}

            <div className="notification-bell-container">
              <button
                className={`notification-bell-btn ${unreadCount > 0 ? 'has-notifications' : ''}`}
                title={isAdminRoute ? t('admin.notifications') : t('layout.notifications')}
                onClick={() => setShowNotifications(!showNotifications)}
              >
                {bellIconSvg}
                {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
              </button>
              {showNotifications && (
                <div className="notification-dropdown">
                  <NotificationListPanel
                    notifications={notifications}
                    variant="dropdown"
                    emptyLabelKey={
                      isAdminRoute ? 'admin.noNotifications' : 'layout.noNotifications'
                    }
                    onMarkRead={markAsRead}
                    onMarkAllRead={markAllAsRead}
                    onItemClick={(n) => {
                      if (n.vacanteId) {
                        navigate(isAdminRoute ? '/admin' : `/recruiter?highlight=${n.vacanteId}`)
                      }
                      setShowNotifications(false)
                    }}
                    showActions
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="recruiter-layout-content">
          <Outlet />
        </div>
      </main>

      <AppModal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title={t('modal.logoutTitle')}
        children={t('modal.logoutMessage')}
        icon={logoutIconSvg}
        onConfirm={() => {
          setShowLogoutModal(false)
          handleLogout()
        }}
        confirmLabel={t('modal.logoutConfirm')}
        confirmVariant="danger"
        showCancel
        cancelLabel={t('modal.cancel')}
      />
      {!isAdminRoute && React.createElement(OnboardingGuide)}
    </div>
  )
}
